import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { fmtMoney0, fmtDate, clotureDepassee, statutLabel, todayISO, DEVIS_STATUTS, STATUTS_PARKES } from "../lib/format";
import { Spinner, Modal, Field } from "../components/ui";
import { RefSelect } from "../components/RefSelect";

const COLS = ["brouillon", "envoye", "negociation", "accepte", "gagne_attente", "gagne"];

export default function Pipeline() {
  const { user } = useAuth();
  const [devis, setDevis] = useState<any[] | null>(null);
  const [refs, setRefs] = useState<any>(null);
  const [dragged, setDragged] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const [zoneOuverte, setZoneOuverte] = useState(false);
  const [parcage, setParcage] = useState<{ devis: any; statut: string } | null>(null);
  const [form, setForm] = useState<any>({});
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const mgr = user?.role === "manager";

  const load = () => api.get("/devis").then((r) => setDevis(r.data));
  useEffect(() => {
    load();
    api.get("/referentiels").then((r) => setRefs(r.data));
  }, []);

  if (!devis || !refs) return <Spinner />;

  const motifs = refs.listes?.motif_perte || [];
  const motifAutre = motifs.find((m: any) => m.code === "autre")?.libelle || "Autre";
  const remplacer = (row: any) => setDevis((prev) => (prev || []).map((x) => (x.id === row.id ? { ...x, ...row } : x)));

  const drop = async (statut: string) => {
    setOver(null);
    const id = dragged;
    setDragged(null);
    if (!id) return;
    const d = devis.find((x) => x.id === id);
    if (!d || d.statut === statut) return;
    if (STATUTS_PARKES.includes(statut)) {
      setErr(null);
      setForm({ motif_perte: null, motif_perte_commentaire: "", date_cloture_estimee: "" });
      setParcage({ devis: d, statut });
      return;
    }
    const prob = DEVIS_STATUTS.find((s) => s.key === statut)?.prob ?? 0;
    remplacer({ id, statut, probabilite: prob });
    try {
      const { data } = await api.patch(`/devis/${id}/statut`, { statut });
      remplacer(data);
    } catch (e) {
      alert(apiError(e));
      load();
    }
  };

  const confirmerParcage = async () => {
    if (!parcage) return;
    setBusy(true);
    setErr(null);
    try {
      const body: any = { statut: parcage.statut };
      if (parcage.statut === "perdu") {
        body.motif_perte = form.motif_perte;
        body.motif_perte_commentaire = form.motif_perte_commentaire || null;
      } else body.date_cloture_estimee = form.date_cloture_estimee || null;
      const { data } = await api.patch(`/devis/${parcage.devis.id}/statut`, body);
      remplacer(data);
      setParcage(null);
      setZoneOuverte(true);
    } catch (e) {
      setErr(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const reactiver = async (d: any) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/devis/${d.id}/reactiver`);
      remplacer(data);
    } catch (e) {
      alert(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const dropzoneProps = (col: string) => ({
    onDragOver: (e: React.DragEvent) => {
      e.preventDefault();
      setOver(col);
    },
    onDragLeave: () => setOver(null),
    onDrop: () => drop(col),
  });

  const parques = devis.filter((d) => STATUTS_PARKES.includes(d.statut));
  const nPerdu = parques.filter((d) => d.statut === "perdu").length;
  const nReporte = parques.length - nPerdu;
  const precedent = parcage && parcage.devis.statut;

  return (
    <div data-testid="pipeline-page">
      <h1 className="mb-4 text-2xl font-extrabold text-green">Pipeline</h1>
      <div className="flex gap-3 overflow-x-auto pb-4">
        {COLS.map((col) => {
          const s = DEVIS_STATUTS.find((x) => x.key === col)!;
          const items = devis.filter((d) => d.statut === col);
          const total = items.reduce((acc, d) => acc + (+d.total_ht || 0) * (+d.probabilite || 0) / 100, 0);
          return (
            <div
              key={col}
              data-testid={`pipeline-col-${col}`}
              className={`kanban-col flex-1 rounded-xl border p-2 transition-colors ${over === col ? "border-lime bg-lime-soft" : "border-line bg-soft/50"}`}
              {...dropzoneProps(col)}
            >
              <div className="mb-2 px-1">
                <p className="text-xs font-bold uppercase tracking-wide text-green">{s.label}</p>
                <p className="text-[11px] text-muted">
                  {items.length} devis{mgr && ` · pondéré ${fmtMoney0(total)}`}
                </p>
              </div>
              <div className="space-y-2">
                {items.map((d) => (
                  <div
                    key={d.id}
                    draggable
                    data-testid={`pipeline-card-${d.numero}`}
                    onDragStart={() => setDragged(d.id)}
                    onDragEnd={() => setDragged(null)}
                    onClick={() => nav(`/devis/${d.id}`)}
                    className="nx-card cursor-grab p-2.5 transition-transform hover:-translate-y-0.5 active:cursor-grabbing"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-extrabold text-green">N° {d.numero}</span>
                      <span className="rounded-full bg-soft px-1.5 text-[10px] font-bold text-muted">{d.probabilite} %</span>
                    </div>
                    <p className="mt-1 line-clamp-2 text-xs font-medium text-ink">{d.objet}</p>
                    <p className="mt-1 text-[11px] text-muted">{d.client_nom}</p>
                    {mgr && <p className="mt-1 text-xs font-bold text-green">{fmtMoney0(d.total_ht, d.devise)}</p>}
                    {d.date_cloture_estimee && (
                      <p data-testid={`pipeline-cloture-${d.numero}`} className={`mt-1 text-[10px] ${clotureDepassee(d) ? "font-semibold text-amber" : "text-muted"}`}>
                        Clôture est. {fmtDate(d.date_cloture_estimee)}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div data-testid="pipeline-zone-parkes" className={`rounded-xl border bg-white transition-colors ${dragged ? "border-lime" : "border-line"}`}>
        <button data-testid="pipeline-zone-toggle" className="flex w-full items-center gap-2 px-3 py-2 text-left" onClick={() => setZoneOuverte(!zoneOuverte)}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className={`text-muted transition-transform ${zoneOuverte ? "rotate-90" : ""}`}><path d="m9 6 6 6-6 6" /></svg>
          <span className="text-xs font-bold uppercase tracking-wide text-muted">Perdu · Reporté</span>
          <span data-testid="pipeline-zone-count" className="rounded-full bg-soft px-2 py-0.5 text-[11px] font-bold text-muted">
            {nPerdu} perdu{nPerdu > 1 ? "s" : ""} · {nReporte} reporté{nReporte > 1 ? "s" : ""}
          </span>
          <span className="ml-auto text-[11px] text-muted">{dragged ? "Déposez la carte ci-dessous" : "Hors pondération du pipeline"}</span>
        </button>
        {(zoneOuverte || dragged) && (
          <div className="grid grid-cols-1 gap-3 border-t border-line p-3 md:grid-cols-2">
            {STATUTS_PARKES.map((col) => {
              const items = parques.filter((d) => d.statut === col);
              return (
                <div
                  key={col}
                  data-testid={`pipeline-col-${col}`}
                  className={`rounded-xl border p-2 transition-colors ${over === col ? "border-lime bg-lime-soft" : "border-dashed border-line bg-soft/40"}`}
                  {...dropzoneProps(col)}
                >
                  <div className="mb-2 flex items-baseline justify-between px-1">
                    <p className="text-xs font-bold uppercase tracking-wide text-muted">{statutLabel(col)}</p>
                    <p className="text-[11px] text-muted">{items.length} devis</p>
                  </div>
                  {dragged && <p className="rounded-lg border border-dashed border-lime px-2 py-3 text-center text-xs text-muted">Déposer ici pour marquer « {statutLabel(col)} »</p>}
                  {zoneOuverte && (
                    <div className="mt-2 grid grid-cols-1 gap-2 lg:grid-cols-2">
                      {items.map((d) => (
                        <div key={d.id} data-testid={`pipeline-card-${d.numero}`} className="nx-card p-2.5">
                          <div className="flex items-center justify-between">
                            <button className="text-xs font-extrabold text-green underline-offset-2 hover:underline" onClick={() => nav(`/devis/${d.id}`)}>N° {d.numero}</button>
                            <span className="rounded-full bg-soft px-1.5 text-[10px] font-bold text-muted">{d.probabilite} %</span>
                          </div>
                          <p className="mt-1 line-clamp-1 text-xs font-medium text-ink">{d.objet}</p>
                          <p className="text-[11px] text-muted">{d.client_nom}{mgr && ` · ${fmtMoney0(d.total_ht, d.devise)}`}</p>
                          {col === "perdu" ? (
                            <p data-testid={`pipeline-motif-${d.numero}`} className="mt-1.5 text-[11px] text-danger">
                              <span className="font-semibold">Motif : {d.motif_perte || "non renseigné"}</span>
                              {d.motif_perte_commentaire && <span className="text-muted"> — {d.motif_perte_commentaire}</span>}
                              {d.date_perte && <span className="text-muted"> · perdu le {fmtDate(d.date_perte)}</span>}
                            </p>
                          ) : (
                            <p data-testid={`pipeline-report-${d.numero}`} className="mt-1.5 text-[11px] font-semibold text-amber">
                              Reporté au {d.date_cloture_estimee ? fmtDate(d.date_cloture_estimee) : "—"}
                            </p>
                          )}
                          <button
                            data-testid={`pipeline-reactiver-${d.numero}`}
                            className="nx-btn-ghost mt-2 w-full !px-2 !py-1 text-xs"
                            disabled={busy}
                            onClick={() => reactiver(d)}
                          >
                            Réactiver → {statutLabel(d.statut_precedent || "envoye")}
                          </button>
                        </div>
                      ))}
                      {items.length === 0 && <p className="px-1 py-2 text-xs text-muted">Aucun devis</p>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {parcage && (
        <Modal
          title={`Devis N° ${parcage.devis.numero} — ${parcage.statut === "perdu" ? "Marquer perdu" : "Reporter"}`}
          onClose={() => setParcage(null)}
          testId="parcage-modal"
        >
          <p className="mb-3 text-sm text-muted">
            {parcage.devis.objet} · {parcage.devis.client_nom} · statut actuel « {statutLabel(precedent)} »
          </p>
          <div className="space-y-3">
            {parcage.statut === "perdu" ? (
              <>
                <Field label="Motif de perte *">
                  <RefSelect testId="parcage-motif-select" entries={motifs} value={form.motif_perte} onChange={(v) => setForm({ ...form, motif_perte: v })} placeholder="Choisir un motif…" />
                </Field>
                <Field label={form.motif_perte === motifAutre ? "Commentaire (obligatoire pour « Autre »)" : "Commentaire (facultatif)"}>
                  <textarea data-testid="parcage-commentaire-input" className="nx-input" rows={2} value={form.motif_perte_commentaire} onChange={(e) => setForm({ ...form, motif_perte_commentaire: e.target.value })} />
                </Field>
                <p className="text-xs text-muted">Probabilité forcée à 0 %. Le devis sort des colonnes actives ; le motif est conservé en historique, même après réactivation.</p>
              </>
            ) : (
              <>
                <Field label="Nouvelle date de clôture estimée *">
                  <input data-testid="parcage-date-input" type="date" min={todayISO()} className="nx-input" value={form.date_cloture_estimee} onChange={(e) => setForm({ ...form, date_cloture_estimee: e.target.value })} />
                </Field>
                <p className="text-xs text-muted">
                  Probabilité 20 % pendant le report. À la réactivation, le devis retrouve le statut « {statutLabel(precedent)} » et sa probabilité ({DEVIS_STATUTS.find((s) => s.key === precedent)?.prob ?? "—"} %).
                </p>
              </>
            )}
          </div>
          {err && <p data-testid="parcage-error" className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-danger">{err}</p>}
          <div className="mt-4 flex justify-end gap-2">
            <button data-testid="parcage-cancel-btn" className="nx-btn-ghost" onClick={() => setParcage(null)}>Annuler</button>
            <button data-testid="parcage-confirm-btn" className={parcage.statut === "perdu" ? "nx-btn-danger" : "nx-btn-primary"} disabled={busy} onClick={confirmerParcage}>
              {busy ? "Enregistrement…" : "Confirmer"}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
